import { useEffect, useState } from "react";
import { Coin1 } from "iconsax-react";
import { useTranslation } from "react-i18next";
import coinIcon from "../../asset/icons/coin.svg";
import { incentivesApi } from "./api";
import { formatNumber, opportunityAmount } from "./helpers";
import "./styles.scss";

const numberLocale = (language) =>
    String(language || "fa").startsWith("fa") ? "fa-IR" : "en-US";

export function useRewardOpportunity({
    action,
    targetType,
    targetId,
    enabled = true,
}) {
    const [state, setState] = useState({
        loading: false,
        opportunity: null,
        error: false,
    });
    useEffect(() => {
        if (!enabled || !action || !targetId) return;
        let active = true;
        setState({ loading: true, opportunity: null, error: false });
        incentivesApi
            .opportunity({
                action,
                target_type: targetType,
                target_id: targetId,
            })
            .then((opportunity) => {
                if (active) {
                    setState({
                        loading: false,
                        opportunity:
                            opportunity?.active === false ? null : opportunity,
                        error: false,
                    });
                }
            })
            .catch(() => {
                if (active) {
                    setState({
                        loading: false,
                        opportunity: null,
                        error: true,
                    });
                }
            });
        return () => {
            active = false;
        };
    }, [action, enabled, targetId, targetType]);
    return state;
}

export function RewardChip({ opportunity, className = "" }) {
    const { t, i18n } = useTranslation();
    const amounts = opportunityAmount(opportunity);
    if (!amounts) return null;
    const locale = numberLocale(i18n.language);
    const ranged = amounts.maximum !== amounts.minimum;
    const display = formatNumber(
        ranged ? amounts.maximum : amounts.minimum,
        locale
    );
    const label = ranged
        ? t("incentives.reward.upTo", { amount: display })
        : t("incentives.reward.fixed", { amount: display });
    return (
        <aside
            className={`reward-chip ${className}`.trim()}
            aria-label={label}
            aria-live="polite"
        >
            <span className="reward-chip-copy">
                <span className="reward-chip-label">
                    {t("incentives.reward.chipLabel")}:
                </span>
                <span className="reward-chip-amount">
                    {ranged ? (
                        <span className="reward-chip-prefix">
                            {t("incentives.reward.upToPrefix")}
                        </span>
                    ) : null}
                    {display}
                </span>
            </span>
            <img src={coinIcon} alt="" width={22} height={22} aria-hidden="true" />
        </aside>
    );
}

export function RewardBadge({
    action,
    targetType,
    targetId,
    opportunity: providedOpportunity,
    className,
}) {
    const fetched = useRewardOpportunity({
        action,
        targetType,
        targetId,
        enabled: providedOpportunity == null,
    });
    const opportunity = providedOpportunity ?? fetched.opportunity;
    return <RewardChip opportunity={opportunity} className={className} />;
}

export function RewardStatus({
    rewardId,
    action,
    targetType,
    targetId,
    status: providedStatus,
}) {
    const { t } = useTranslation();
    const [status, setStatus] = useState(providedStatus);
    useEffect(() => {
        setStatus(providedStatus);
    }, [providedStatus]);
    useEffect(() => {
        if (providedStatus || (!rewardId && !targetId)) return;
        incentivesApi
            .rewardStatus({
                reward_id: rewardId,
                action,
                target_type: targetType,
                target_id: targetId,
            })
            .then((result) =>
                setStatus(
                    result?.status && result.status !== "none"
                        ? result.status
                        : null
                )
            )
            .catch(() => {});
    }, [action, providedStatus, rewardId, targetId, targetType]);
    if (!status) return null;
    const normalized = String(status).toLowerCase();
    return (
        <div
            className={`reward-status reward-status-${normalized}`}
            role="status"
        >
            <Coin1 size={18} aria-hidden="true" />
            <span>
                {t(`incentives.reward.status.${normalized}`, {
                    defaultValue: normalized,
                })}
            </span>
        </div>
    );
}
